import { useSetAtom } from "jotai";
import { useQueryClient } from "@tanstack/react-query";
import { posthog } from "posthog-js";
import * as Sentry from "@sentry/react";
import { useSearchParams } from "react-router-dom";

import {
  applicationPageVar,
  messagesVar,
  Pages,
  Sections,
  wizardNavigatorVar,
} from "@/variables/globalVar";

import {
  getAccountQueryKey,
  useCancelOrCompleteCurrentApplication,
} from "shared/apiHooks";
import { CaptureEventType, useAccount, useCaptureEvent } from "@/hooks";

export interface UseCancelOrCompleteApplicationOptions {
  onError?: Parameters<
    typeof useCancelOrCompleteCurrentApplication
  >[0]["onError"];
  onSuccess?: Parameters<
    typeof useCancelOrCompleteCurrentApplication
  >[0]["onSuccess"];
}

export function useCancelOrCompleteApplication(
  options?: UseCancelOrCompleteApplicationOptions,
) {
  const queryClient = useQueryClient();
  const [_, setSearchParams] = useSearchParams();

  const { captureEvent } = useCaptureEvent();

  const setMessagesVar = useSetAtom(messagesVar);
  const setWizardNavigatorVar = useSetAtom(wizardNavigatorVar);
  const setApplicationPageVar = useSetAtom(applicationPageVar);

  const { account } = useAccount();
  const email = account?.email || "";

  const cancelOrCompleteApplicationApi = useCancelOrCompleteCurrentApplication({
    onError: (...args) => {
      console.error("useCancelOrCompleteApplicationApi onError()", args[0]);

      if (args[0]?.response?.status === 404) {
        // Application not found - reload their account
        posthog.reset();
        posthog.identify(email, {
          email,
          uuid: account?.id,
        });
        Sentry.setUser({ email });

        setMessagesVar([]);
        setApplicationPageVar(Pages.Address);
        setWizardNavigatorVar(Sections.Address);
        queryClient.invalidateQueries({ queryKey: getAccountQueryKey() });
      }

      options?.onError?.(...args);
    },
    onSuccess: (...args) => {
      console.log("useCancelOrCompleteApplicationApi onSuccess", args[0].data);

      captureEvent(CaptureEventType.USER_RESTARTED_CONVERSATION);

      posthog.reset();
      posthog.identify(email, {
        email,
        uuid: account?.id,
      });
      Sentry.setUser({ email });

      setMessagesVar([]);
      setApplicationPageVar(Pages.Address);
      setWizardNavigatorVar(Sections.Address);
      queryClient.invalidateQueries({ queryKey: getAccountQueryKey() });

      setSearchParams((prev) => {
        prev.delete("selectedMenuItem");
        return prev;
      });

      options?.onSuccess?.(...args);
    },
  });

  const cancelOrCompleteApplication = () => {
    return cancelOrCompleteApplicationApi.mutate();
  };

  return {
    ...cancelOrCompleteApplicationApi,
    cancelOrCompleteApplication,
  };
}
